import { useRouter } from 'next/navigation';
import React from 'react';

import {
  advancedSearchCountyStorageKey,
  advancedSearchStateStorageKey,
} from '@/constants/localStorage';
import {
  ADVANCED_SEARCH_URL,
  COMPS_URL,
  INSIGHTS_CATALOG_LIBRARY_AREA_URL,
  INSIGHTS_CATALOG_URL,
  LISTINGS_URL,
  MORTGAGE_URL,
  SAVED_LAND_PLAT_MAP_URL,
} from '@/constants/urls';
import { County } from '@/endpoints/itasca/county/getCounties';
import { ReactSetState } from '@/models/reactTypes';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import zoomToBounds from '@/utils/zoomToBounds';

import { IndexedSearchResult, SearchResult } from './models';
import { SearchElement } from './searchElements';
import { useLayersSearch } from './searchHooks/useLayersSearch';
import { useSavedMapsSearch } from './searchHooks/useSavedMapsSearch';
import { SearchResultsSection } from './searchResultsSection';

type Props = {
  selectedCounty: IndexedSearchResult<County>;
  cursor: number;
  setCursor: ReactSetState<number>;
  trackResultClicked: (
    result: SearchResult<any>,
    hardcoded_link?: string
  ) => void;
};

export const CountyDropdown: React.FC<Props> = ({
  selectedCounty,
  cursor,
  setCursor,
  trackResultClicked,
}) => {
  const router = useRouter();
  const { mapInstance: map } = useMapboxContext();

  const {
    layers,
    layersSeeMore,
    isLoading: layersLoading,
  } = useLayersSearch('', {
    selectedCounty: selectedCounty.original,
  });

  const {
    saves,
    savesSeeMore,
    isLoading: savesLoading,
  } = useSavedMapsSearch('', {
    selectedCounty: selectedCounty.original,
  });

  return (
    <div className="flex flex-col divide-y-1 divide-solid divide-gray-20">
      <SearchResultsSection title="Go To">
        <SearchElement
          focus={cursor === 0}
          index={0}
          value={selectedCounty.label}
          onClick={() => {
            if (!map) {
              return;
            }

            zoomToBounds(map, selectedCounty.geometry);
            trackResultClicked(selectedCounty);
          }}
          onMouseEnter={() => setCursor(0)}
        />
      </SearchResultsSection>
      <SearchResultsSection title="Advanced Search">
        <SearchElement
          focus={cursor === 1}
          index={1}
          value={selectedCounty.label}
          onClick={() => {
            trackResultClicked(selectedCounty, ADVANCED_SEARCH_URL);

            // To be temp consumed by advanced search

            // Stash the state
            sessionStorage.setItem(
              advancedSearchStateStorageKey,
              JSON.stringify({
                name: selectedCounty.original.state_name,
                state_abbrev: selectedCounty.original.state_abbrev,
                state_fips: selectedCounty.original.state_fips,
              })
            );

            // Stash the county
            const countyCopy = { ...selectedCounty.original };
            delete countyCopy.geometry;
            sessionStorage.setItem(
              advancedSearchCountyStorageKey,
              JSON.stringify({
                ...countyCopy,
              })
            );

            router.push(ADVANCED_SEARCH_URL);

            if (!map) return;

            zoomToBounds(map, selectedCounty.geometry);
          }}
          onMouseEnter={() => setCursor(1)}
        />
      </SearchResultsSection>
      <SearchResultsSection title="Listings">
        <SearchElement
          focus={cursor === 2}
          index={2}
          value={selectedCounty.label}
          onClick={() => {
            trackResultClicked(selectedCounty, LISTINGS_URL);
            router.push(
              `${LISTINGS_URL}/${selectedCounty.original.state_abbrev}/${selectedCounty.original.county_state_slug}`
            );

            if (!map) return;

            zoomToBounds(map, selectedCounty.geometry);
          }}
          onMouseEnter={() => setCursor(2)}
        />
      </SearchResultsSection>
      <SearchResultsSection title="Sold Land">
        <SearchElement
          focus={cursor === 3}
          index={3}
          value={selectedCounty.label}
          onClick={() => {
            trackResultClicked(selectedCounty, COMPS_URL);
            router.push(
              `${COMPS_URL}/${selectedCounty.original.state_abbrev}/${selectedCounty.original.county_state_slug}`
            );

            if (!map) return;
            zoomToBounds(map, selectedCounty.geometry);
          }}
          onMouseEnter={() => setCursor(3)}
        />
      </SearchResultsSection>
      <SearchResultsSection title="Mortgage">
        <SearchElement
          focus={cursor === 4}
          index={4}
          value={selectedCounty.label}
          onClick={() => {
            trackResultClicked(selectedCounty, MORTGAGE_URL);
            router.push(
              `${MORTGAGE_URL}/${selectedCounty.original.state_abbrev}/${selectedCounty.original.county_state_slug}`
            );
            if (!map) return;
            zoomToBounds(map, selectedCounty.geometry);
          }}
          onMouseEnter={() => setCursor(4)}
        />
      </SearchResultsSection>
      <SearchResultsSection
        loading={layersLoading}
        noResults={!layers?.length}
        seeMoreResults={{
          count: layersSeeMore,
          url: `${INSIGHTS_CATALOG_LIBRARY_AREA_URL}?stateFips=${selectedCounty.original.state_fips}&countyFips=${selectedCounty.original.county_fips}`,
        }}
        title="Layers"
      >
        {layers?.map((layer, index) => (
          <SearchElement
            key={layer.id}
            focus={cursor === index + 5}
            index={index + 5}
            value={layer.label}
            onClick={() => {
              trackResultClicked(layer as SearchResult<any>);
              if (map) zoomToBounds(map, selectedCounty.geometry);
              router.push(`${INSIGHTS_CATALOG_URL}/${layer.original.id}/`);
            }}
            onMouseEnter={() => setCursor(index + 5)}
          />
        ))}
      </SearchResultsSection>
      <SearchResultsSection
        loading={savesLoading}
        noResults={!saves?.length}
        seeMoreResults={{
          count: savesSeeMore,
          url: `${SAVED_LAND_PLAT_MAP_URL}?countyFips=${selectedCounty.original.county_fips}&stateFips=${selectedCounty.original.state_fips}`,
        }}
        title="Saved Maps"
      >
        {saves?.map((save, index) => (
          <SearchElement
            key={save.id}
            focus={cursor === index + (layers.length ?? 0) + 5}
            index={index + (layers.length ?? 0) + 5}
            value={save.label}
            onClick={() => {
              trackResultClicked(save as SearchResult<any>);
              if (map) zoomToBounds(map, selectedCounty.geometry);
              router.push(save.url);
            }}
            onMouseEnter={() => setCursor(index + (layers.length ?? 0) + 5)}
          />
        ))}
      </SearchResultsSection>
    </div>
  );
};
